import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { ContentLocalizationPortal } from "../../i18n/ContentLocalizationPortal";
import { LocalizationSettingsPortal } from "../../i18n/LocalizationSettingsPortal";
import { ProfileLanguageRegionPortal } from "../../i18n/ProfileLanguageRegionPortal";
import { pushNavigation } from "../../navigation";
import { KeysTrustManagedPage } from "./KeysTrustManagedPage";
import { useKeysTrustManagedCopy } from "./keysTrustManagedCatalog";
import { KeysTrustNavigationPortal } from "./KeysTrustNavigationPortal";

function adminMain(): HTMLElement | null {
  return document.getElementById("admin-v12-main");
}

export function PlatformBrandingSettingsPortal({ path }: { path: string }) {
  const { t: keysTrustT } = useKeysTrustManagedCopy();
  const [keysTrustHost, setKeysTrustHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (path !== "/admin/keys-trust") {
      setKeysTrustHost(null);
      return;
    }

    let mountedMain: HTMLElement | null = null;
    let mountedHost: HTMLElement | null = null;
    const previousTitle = document.title;

    const mount = () => {
      const main = adminMain();
      if (!main) return;
      const existing = document.getElementById("keys-trust-admin-slot");
      if (existing instanceof HTMLElement) {
        mountedMain = main;
        mountedHost = existing;
        main.dataset.keysTrustActive = "true";
        setKeysTrustHost(existing);
        document.title = `${keysTrustT("title")} · Kavoosh Admin`;
        return;
      }

      const slot = document.createElement("section");
      slot.id = "keys-trust-admin-slot";
      slot.className = "keys-trust-admin-slot";
      slot.setAttribute("aria-label", keysTrustT("title"));
      main.appendChild(slot);
      main.dataset.keysTrustActive = "true";
      mountedMain = main;
      mountedHost = slot;
      setKeysTrustHost(slot);
      document.title = `${keysTrustT("title")} · Kavoosh Admin`;
    };

    mount();
    const observer = new MutationObserver(mount);
    observer.observe(document.getElementById("root") ?? document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      if (mountedMain) delete mountedMain.dataset.keysTrustActive;
      if (mountedHost?.isConnected) mountedHost.remove();
      setKeysTrustHost(null);
      document.title = previousTitle;
    };
  }, [path, keysTrustT]);

  const sessionUnavailable = () => pushNavigation("/login");

  return <>
    {keysTrustHost ? createPortal(
      <KeysTrustManagedPage onSessionUnavailable={sessionUnavailable} />,
      keysTrustHost,
    ) : null}
    <KeysTrustNavigationPortal path={path} />
    <LocalizationSettingsPortal path={path} />
    <ProfileLanguageRegionPortal path={path} />
    <ContentLocalizationPortal path={path} />
  </>;
}
